import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { AppShell } from './shell/AppShell'
import { useSession } from '@/stores/session'
import { Lens } from '@/design-system'
import { HomeScreen } from '@/features/cats/HomeScreen'
import { LoginScreen, ForgotPasswordScreen, NewPasswordScreen } from '@/features/auth/LoginScreen'
import { RegisterScreen } from '@/features/auth/RegisterScreen'
import { ConfirmEmailScreen } from '@/features/auth/ConfirmEmailScreen'
import { RouteErrorScreen } from './ErrorBoundary'

// The camera, the map and the model runtime are the heavy parts of the app.
// None of them load until somebody actually goes there.
const CaptureScreen = lazy(() =>
  import('@/features/camera/CaptureScreen').then((m) => ({ default: m.CaptureScreen })),
)
const MapScreen = lazy(() =>
  import('@/features/maps/MapScreen').then((m) => ({ default: m.MapScreen })),
)
const CatProfileScreen = lazy(() =>
  import('@/features/cats/CatProfileScreen').then((m) => ({ default: m.CatProfileScreen })),
)
const GalleryScreen = lazy(() =>
  import('@/features/cats/GalleryScreen').then((m) => ({ default: m.GalleryScreen })),
)
const CollectionScreen = lazy(() =>
  import('@/features/cats/CollectionScreen').then((m) => ({ default: m.CollectionScreen })),
)
const DiscoverScreen = lazy(() =>
  import('@/features/discover/DiscoverScreen').then((m) => ({ default: m.DiscoverScreen })),
)
const SearchScreen = lazy(() =>
  import('@/features/discover/SearchScreen').then((m) => ({ default: m.SearchScreen })),
)
const UserProfileScreen = lazy(() =>
  import('@/features/social/UserProfileScreen').then((m) => ({ default: m.UserProfileScreen })),
)
const AchievementsScreen = lazy(() =>
  import('@/features/achievements/AchievementsScreen').then((m) => ({ default: m.AchievementsScreen })),
)
const MyProfileScreen = lazy(() =>
  import('@/features/social/MyProfileScreen').then((m) => ({ default: m.MyProfileScreen })),
)
const EditProfileScreen = lazy(() =>
  import('@/features/social/EditProfileScreen').then((m) => ({ default: m.EditProfileScreen })),
)
const RequestsScreen = lazy(() =>
  import('@/features/social/RequestsScreen').then((m) => ({ default: m.RequestsScreen })),
)
const RelationsScreen = lazy(() =>
  import('@/features/social/RelationsScreen').then((m) => ({ default: m.RelationsScreen })),
)
const SettingsScreen = lazy(() =>
  import('@/features/settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
)
const PrivacyScreen = lazy(() =>
  import('@/features/settings/PrivacyScreen').then((m) => ({ default: m.PrivacyScreen })),
)
const MailboxScreen = lazy(() =>
  import('@/features/settings/MailboxScreen').then((m) => ({ default: m.MailboxScreen })),
)
const NotificationsScreen = lazy(() =>
  import('@/features/notifications/NotificationsScreen').then((m) => ({ default: m.NotificationsScreen })),
)
const PendingScreen = lazy(() =>
  import('@/features/offline/PendingScreen').then((m) => ({ default: m.PendingScreen })),
)
const DuplicatesScreen = lazy(() =>
  import('@/features/identity/DuplicatesScreen').then((m) => ({ default: m.DuplicatesScreen })),
)
const OnboardingScreen = lazy(() =>
  import('@/features/onboarding/OnboardingScreen').then((m) => ({ default: m.OnboardingScreen })),
)
const InstallScreen = lazy(() =>
  import('@/features/onboarding/InstallScreen').then((m) => ({ default: m.InstallScreen })),
)

// Públicas: se leen sin sesión, desde el registro, Ajustes o la web. (§H261)
const PrivacyNoticeScreen = lazy(() =>
  import('@/features/legal/LegalScreen').then((m) => ({ default: m.PrivacyNoticeScreen })),
)
const TermsScreen = lazy(() =>
  import('@/features/legal/LegalScreen').then((m) => ({ default: m.TermsScreen })),
)

function Loading() {
  return (
    <div
      style={{
        minHeight: '60svh',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--text-muted)',
      }}
    >
      <Lens size={28} />
    </div>
  )
}

function B({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>
}

/** Nothing behind this renders until we know whether there is a session. */
function RequireAuth() {
  const ready = useSession((s) => s.ready)
  const user = useSession((s) => s.user)
  if (!ready) return <Loading />
  if (!user) return <Navigate to="/entrar" replace />
  return <Outlet />
}

function RequireAnon() {
  const ready = useSession((s) => s.ready)
  const user = useSession((s) => s.user)
  if (!ready) return <Loading />
  if (user) return <Navigate to="/" replace />
  return <Outlet />
}

/**
 * /c/<código> es la página pública de un gato compartido y la sirve la web, no
 * la app (§0084). Solo se llega aquí si una versión vieja de la app instalada
 * se quedó con el enlace: se pide la nueva, que al tomar el control recarga
 * sola la página (sw-registration) y el enlace llega por fin a la web.
 */
function SharedCatFallback() {
  const [tarde, setTarde] = useState(false)
  useEffect(() => {
    void navigator.serviceWorker
      ?.getRegistration()
      .then((r) => r?.update())
      .catch(() => undefined)
    const t = window.setTimeout(() => setTarde(true), 6000)
    return () => window.clearTimeout(t)
  }, [])
  return (
    <main className="page page--nodock">
      <div className="empty">
        <Lens size={28} />
        <h1 className="empty__title">Abriendo el gato compartido…</h1>
        {tarde && (
          <a className="btn btn--secondary" href={window.location.href} style={{ marginBlockStart: 'var(--s3)' }}>
            Volver a intentarlo
          </a>
        )}
      </div>
    </main>
  )
}

function NotFound() {
  return (
    <main className="page page--nodock">
      <div className="empty">
        <div className="empty__mount" />
        <h1 className="empty__title">Esta página no existe</h1>
        <p className="empty__body">Puede que el enlace esté roto o que ya no tengas acceso.</p>
        <a className="btn btn--secondary" href="/" style={{ marginBlockStart: 'var(--s3)' }}>
          Volver al registro
        </a>
      </div>
    </main>
  )
}

function devRoutes() {
  const DesignReview = lazy(() =>
    import('@/features/dev/DesignReview').then((m) => ({ default: m.DesignReview })),
  )
  return [
    { path: '/_design', element: <B><DesignReview /></B> },
    { path: '/_design/bienvenida', element: <B><OnboardingScreen /></B> },
    { path: '/_design/instalar', element: <B><InstallScreen /></B> },
  ]
}

export const router = createBrowserRouter([
  {
    element: <RequireAnon />,
    errorElement: <RouteErrorScreen />,
    children: [
      { path: '/entrar', element: <LoginScreen /> },
      { path: '/crear-cuenta', element: <RegisterScreen /> },
      { path: '/confirma-correo', element: <ConfirmEmailScreen /> },
      { path: '/recuperar', element: <ForgotPasswordScreen /> },
    ],
  },
  { path: '/nueva-contrasena', element: <NewPasswordScreen />, errorElement: <RouteErrorScreen /> },
  { path: '/privacidad', element: <B><PrivacyNoticeScreen /></B>, errorElement: <RouteErrorScreen /> },
  { path: '/c/:token', element: <SharedCatFallback /> },
  { path: '/condiciones', element: <B><TermsScreen /></B>, errorElement: <RouteErrorScreen /> },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorScreen />,
    children: [
      { path: '/bienvenida', element: <B><OnboardingScreen /></B> },
      { path: '/instalar', element: <B><InstallScreen /></B> },
      // Full screen: the camera has no dock over it.
      { path: '/capturar', element: <B><CaptureScreen /></B> },
      {
        element: <AppShell />,
        children: [
          { path: '/', element: <HomeScreen /> },
          { path: '/coleccion', element: <B><CollectionScreen /></B> },
          { path: '/gato/:catId', element: <B><CatProfileScreen /></B> },
          { path: '/gato/:catId/galeria', element: <B><GalleryScreen /></B> },
          { path: '/mapa', element: <B><MapScreen /></B> },
          { path: '/descubrir', element: <B><DiscoverScreen /></B> },
          { path: '/buscar', element: <B><SearchScreen /></B> },
          { path: '/u/:username', element: <B><UserProfileScreen /></B> },
          { path: '/perfil', element: <B><MyProfileScreen /></B> },
      { path: '/logros', element: <B><AchievementsScreen /></B> },
      { path: '/logros/:userId', element: <B><AchievementsScreen /></B> },
          { path: '/perfil/editar', element: <B><EditProfileScreen /></B> },
          { path: '/perfil/solicitudes', element: <B><RequestsScreen /></B> },
          { path: '/perfil/seguidores', element: <B><RelationsScreen kind="followers" /></B> },
          { path: '/perfil/siguiendo', element: <B><RelationsScreen kind="following" /></B> },
          { path: '/perfil/mejores-amigos', element: <B><RelationsScreen kind="close_friends" /></B> },
          { path: '/perfil/bloqueados', element: <B><RelationsScreen kind="blocked" /></B> },
          { path: '/ajustes', element: <B><SettingsScreen /></B> },
          { path: '/ajustes/privacidad', element: <B><PrivacyScreen /></B> },
          { path: '/ajustes/buzon', element: <B><MailboxScreen /></B> },
          { path: '/pendientes', element: <B><PendingScreen /></B> },
          { path: '/avisos', element: <B><NotificationsScreen /></B> },
          { path: '/duplicados', element: <B><DuplicatesScreen /></B> },
        ],
      },
    ],
  },
  // Development only: a gallery for judging the interface with real
  // photographs. Not registered in a production build.
  // Development only: a gallery for judging the interface with real
  // photographs, plus the screens that sit behind a session. The lazy import
  // lives inside the dead branch so the chunk is never emitted in a production
  // build, and public/dev is stripped from dist by the build itself.
  ...(import.meta.env.DEV ? devRoutes() : []),
  { path: '*', element: <NotFound /> },
])
